import { Component } from '@angular/core';

import {
    DOC_TYPES, DocType, FoundDocument, L2_PROCESSES, SOURCE_KEYS, SOURCES, TYPE_ORDER, sourceName
} from '~/models/document-finder.models';
import { DocumentFinderService } from '~/services/document-finder.service';

export interface DocGroup { type: DocType; docs: FoundDocument[]; included: number; allSelected: boolean; }

@Component({
    selector: 'df-review-step',
    templateUrl: './df-review-step.component.html',
    styleUrls: ['./df-review-step.component.scss'],
    standalone: false
})
export class DfReviewStepComponent {
    public readonly docTypes = DOC_TYPES as Record<string, (typeof DOC_TYPES)[keyof typeof DOC_TYPES]>;
    public readonly l2 = L2_PROCESSES;
    public readonly sourceKeys = SOURCE_KEYS;
    public readonly sources = SOURCES as Record<string, (typeof SOURCES)[keyof typeof SOURCES]>;

    constructor(public df: DocumentFinderService) {}

    public readonly trackByKey = (_: number, item: { key: string }) => item.key;
    public readonly trackByType = (_: number, item: DocGroup) => item.type;
    public readonly trackById = (_: number, item: FoundDocument) => item.id;

    public get tabs(): { key: 'all' | 'x' | DocType; label: string; count: number }[] {
        const all0 = this.df.found();
        const all = all0.filter(d => !this.df.agentExcluded(d));
        const xN = all0.length - all.length;
        const visible = this.df.filtered('all');
        const excludedCount = this.df.filtered('x').length;
        const keys: ('all' | 'x' | DocType)[] = ['all', ...TYPE_ORDER.filter(k => all.some(d => d.t === k))];
        if (xN) { keys.push('x'); }
        return keys.map(k => ({
            key: k,
            label: k === 'all' ? 'All' : k === 'x' ? 'Excluded by agent' : k === 'other' ? 'Not an input' : k === 'flow' ? 'Process flows' : DOC_TYPES[k].short,
            count: k === 'all' ? visible.length : k === 'x' ? excludedCount : visible.filter(d => d.t === k).length
        }));
    }

    public get pendingCount(): number {
        return this.df.pending().length;
    }

    public get groups(): DocGroup[] {
        const list = this.df.filtered();
        const types = this.df.tab === 'all' || this.df.tab === 'x' ? TYPE_ORDER : [this.df.tab];
        return types
            .map(type => {
                const docs = list.filter(d => d.t === type);
                return {
                    type,
                    docs,
                    included: docs.filter(d => this.df.decisions[d.id] === 'inc').length,
                    allSelected: docs.length > 0 && docs.every(d => this.df.selection.has(d.id))
                };
            })
            .filter(g => g.docs.length > 0);
    }

    public sourceShort(k: string): string {
        return sourceName(k).split(' ·')[0];
    }

    public setTab(k: 'all' | 'x' | DocType): void {
        this.df.tab = k;
    }

    public get doneCount(): number { return this.df.done.size; }

    public get leftCount(): number { return this.df.filtered().length; }

    public showDecided(): void {
        this.df.showDecidedAgain();
        this.df.toast('Showing decided items again');
    }

    public markAll(type: DocType): void {
        this.df.includeAllOfType(type);
        this.df.toast(`Marked all ${DOC_TYPES[type].short} documents relevant`);
    }

    public selectGroup(type: DocType, checked: boolean): void {
        this.df.toggleSelectGroup(type, checked);
    }

    public bulkInclude(): void {
        const n = this.df.includeSelected();
        this.df.toast(`${n} documents marked relevant`);
    }

    public bulkExclude(): void {
        this.df.openExclude([...this.df.selection]);
    }

    public clearSelection(): void {
        this.df.selection.clear();
    }

    public clearFilters(): void {
        this.df.clearFilters();
    }
}
